import { useEffect, useRef } from 'react';
import { useWorkflowResumeStore } from '../../../shared/stores/useWorkflowResumeStore';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import { buildValidationRepairDetailItems } from '../lib/validationRepairDetailModel';
import type { ValidationWorkspaceState } from './useValidationWorkspace';

export function useValidationResumeSync(
  resumeScopeKey: string | null | undefined,
  workspace: ValidationWorkspaceState
): void {
  const setValidationResume = useWorkflowResumeStore((store) => store.setValidationResume);
  const validationResume = useWorkflowResumeStore((store) =>
    resumeScopeKey ? store.validationByScope[resumeScopeKey] : undefined
  );
  const issueNavigationContext = usePcfValidationStore((store) => store.issueNavigationContext);
  const repairDetailContext = usePcfValidationStore((store) => store.repairDetailContext);
  const conditionStudioContext = usePcfValidationStore((store) => store.conditionStudioContext);
  const openRepairDetail = usePcfValidationStore((store) => store.openRepairDetail);
  const openConditionStudio = usePcfValidationStore((store) => store.openConditionStudio);
  const validationResumeReadyScopeRef = useRef<string | null>(null);

  useEffect(() => {
    if (!resumeScopeKey) {
      return;
    }

    setValidationResume(resumeScopeKey, (current) => ({
      ...current,
      selectedFamilyId: workspace.selectedFamilyId,
      searchQuery: workspace.searchQuery,
      severityFilter: workspace.severityFilter,
      blockerFilter: workspace.blockerFilter,
      requiredOnly: workspace.requiredOnly,
      categoryFilter: workspace.categoryFilter,
      showPassedOnly: workspace.showPassedOnly,
      passedCollapsed: workspace.passedCollapsed,
    }));
  }, [
    resumeScopeKey,
    setValidationResume,
    workspace.blockerFilter,
    workspace.categoryFilter,
    workspace.passedCollapsed,
    workspace.requiredOnly,
    workspace.searchQuery,
    workspace.selectedFamilyId,
    workspace.severityFilter,
    workspace.showPassedOnly,
  ]);

  useEffect(() => {
    if (!resumeScopeKey) {
      return;
    }

    validationResumeReadyScopeRef.current = null;
  }, [resumeScopeKey]);

  useEffect(() => {
    if (!resumeScopeKey) {
      return;
    }

    if (validationResumeReadyScopeRef.current === resumeScopeKey) {
      return;
    }

    if (issueNavigationContext || repairDetailContext || conditionStudioContext) {
      validationResumeReadyScopeRef.current = resumeScopeKey;
      return;
    }

    if (!validationResume) {
      validationResumeReadyScopeRef.current = resumeScopeKey;
      return;
    }

    if (workspace.allFamilies.length === 0 && !workspace.selectedFamilyId) {
      return;
    }

    if (validationResume.surface === 'repairDetail' && validationResume.repairDetail) {
      const resumeFamily = workspace.allFamilies.find(
        (family) => family.id === validationResume.repairDetail?.familyId
      );

      if (!resumeFamily) {
        setValidationResume(resumeScopeKey, (current) => ({
          ...current,
          surface: 'queue',
          repairDetail: null,
        }));
        validationResumeReadyScopeRef.current = resumeScopeKey;
        return;
      }

      const validRepairItems = buildValidationRepairDetailItems(resumeFamily) ?? [];
      const selectedRepairItemId = validationResume.repairDetail.selectedItemId;
      const hasValidSelectedRepairItem =
        !selectedRepairItemId || validRepairItems.some((item) => item.id === selectedRepairItemId);

      if (!hasValidSelectedRepairItem) {
        setValidationResume(resumeScopeKey, (current) => ({
          ...current,
          surface: 'queue',
          repairDetail: null,
        }));
        validationResumeReadyScopeRef.current = resumeScopeKey;
        return;
      }

      openRepairDetail({
        familyId: resumeFamily.id,
        selectedItemId: selectedRepairItemId,
        activeTab: validationResume.repairDetail.activeTab,
        surgeryMode: validationResume.repairDetail.surgeryMode,
      });
      validationResumeReadyScopeRef.current = resumeScopeKey;
      return;
    }

    if (validationResume.surface === 'conditionStudio' && validationResume.conditionStudio) {
      const sourceFamilyId = validationResume.conditionStudio.sourceFamilyId;
      const hasValidSourceFamilyId =
        !sourceFamilyId || workspace.allFamilies.some((family) => family.id === sourceFamilyId);

      if (!hasValidSourceFamilyId) {
        setValidationResume(resumeScopeKey, (current) => ({
          ...current,
          surface: 'queue',
          conditionStudio: null,
        }));
        validationResumeReadyScopeRef.current = resumeScopeKey;
        return;
      }

      openConditionStudio({
        targetKey: validationResume.conditionStudio.targetKey,
        selectedConditionSetId: validationResume.conditionStudio.selectedConditionSetId,
        selectedValidationId: validationResume.conditionStudio.selectedValidationId ?? null,
        sourceFamilyId,
      });
      validationResumeReadyScopeRef.current = resumeScopeKey;
      return;
    }

    validationResumeReadyScopeRef.current = resumeScopeKey;
  }, [
    conditionStudioContext,
    issueNavigationContext,
    openConditionStudio,
    openRepairDetail,
    repairDetailContext,
    resumeScopeKey,
    setValidationResume,
    validationResume,
    workspace.allFamilies,
    workspace.selectedFamilyId,
  ]);

  useEffect(() => {
    if (!resumeScopeKey) {
      return;
    }

    if (validationResumeReadyScopeRef.current !== resumeScopeKey) {
      return;
    }

    setValidationResume(resumeScopeKey, (current) => ({
      ...current,
      surface: repairDetailContext
        ? 'repairDetail'
        : conditionStudioContext
          ? 'conditionStudio'
          : 'queue',
      repairDetail: repairDetailContext
        ? {
            familyId: repairDetailContext.familyId,
            selectedItemId: repairDetailContext.selectedItemId,
            activeTab: repairDetailContext.activeTab,
            surgeryMode: repairDetailContext.surgeryMode,
          }
        : null,
      conditionStudio: conditionStudioContext
        ? {
            targetKey: conditionStudioContext.targetKey,
            selectedConditionSetId: conditionStudioContext.selectedConditionSetId,
            selectedValidationId: conditionStudioContext.selectedValidationId ?? null,
            sourceFamilyId: conditionStudioContext.sourceFamilyId ?? null,
          }
        : null,
    }));
  }, [conditionStudioContext, repairDetailContext, resumeScopeKey, setValidationResume]);
}
